import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { errorTabConfig } from '@core/config/error-tab-config';
import { NotEligibleError, ProductName } from '@core/constants';
import { PlatformService } from '@core/services/platform.service';
import factory from '@core/services/queue/factory';
import { QueueBaseService } from '@core/services/queue/queue-base.service';
import { ODataService } from '@mynexus/mynexus-ui-lib';
import {
  EvaluateData,
  FilterData,
  ServiceLine,
} from '@shared/models/service-line-routing.model';
import { ExpandTableConfig, TabConfig, TableConfig } from '@shared/models/table-config.model';
import buildQuery from 'odata-query';
import { LazyLoadEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { catchError, finalize, map, Observable, shareReplay, Subject, switchMap, tap } from 'rxjs';
import { ErrorMessages } from '../routing-rules/constants';

@Component({
  selector: 'app-queue',
  templateUrl: './queue.component.html',
  styleUrls: ['./queue.component.scss'],
  standalone: false,
})
export class QueueComponent implements OnInit {
  @ViewChild(Table, { static: true }) table!: Table;

  private oDataService = inject(ODataService);
  private platformService = inject(PlatformService);
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);

  config!: ExpandTableConfig;
  columnConfig: TableConfig = [];
  tabs!: TabConfig[];
  dataKey!: string;

  queue$!: Observable<ServiceLine[]>;
  idDMEPOS$ = this.platformService.currentProduct.pipe(
    map((product) => [ProductName.DMEPOS_MA, ProductName.DMEPOS_MCD].includes(product as ProductName))
  );

  isLoading: boolean = true;
  totalRecords: number = 0;
  rowsPerPage: number = 10;
  last: number = 1;
  first: number = 0;
  queueEvaluation$!: Observable<EvaluateData>;
  filtersData: FilterData[] = [];
  filtersText: Record<string, string> = {};
  dropdownOptions$!: Observable<Record<string, any>>;

  private readonly loadQueueData$ = new Subject<LazyLoadEvent>();

  private queueService!: QueueBaseService;

  ngOnInit() {
    this.getProduct();

    this.queue$ = this.loadQueueData$.pipe(
      tap(() => (this.isLoading = true)),
      map((query) => this.oDataService.buildQuery(query)),
      switchMap((query) => this.queueService.query(query)),
      tap((data) => {
        this.totalRecords = data.count;
        this.last = data.items.length;
        this.isLoading = false;
      }),
      map((data) => data.items),
      finalize(() => (this.isLoading = false))
    );
  }

  private getProduct(): void {
    this.platformService.currentProduct.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
      this.queueService = factory(this.platformService, this.http);
      this.dataKey = this.queueService.uniqueKey;
      this.config = await this.platformService.getQueueConfig();
      this.columnConfig = this.config.table;
      this.tabs = this.config.expandedView;

      this.filtersText = this.columnConfig.reduce((text, column) => {
        if (column.filterOptions?.template === 'autoComplete') {
          text[column.dataKey] = '';
        }

        return { ...text };
      }, {} as Record<string, string>);

      this.getDropdownOptions();
    });
  }

  fetchTableData(event: LazyLoadEvent = this.table.createLazyLoadMetadata()): void {
    this.table.expandedRowKeys = {};
    this.loadQueueData$.next(event);
  }

  fetchSuggestions(value: string, prop: string): void {
    const userInput = (value ?? '').toLocaleLowerCase();
    const transform = [
      { filter: { [`tolower(${prop})`]: { contains: userInput } } },
      { groupBy: { properties: [prop] } },
    ];

    const query = buildQuery({ transform, top: 100, skip: 0, count: false });

    this.queueService
      .query(query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ items }) => (this.filtersData = items as any));
  }

  getExpandedData(id: string): void {
    this.tabs = this.config.expandedView;

    this.queueEvaluation$ = this.queueService.getEvaluateQueue(id).pipe(
      catchError((err) => {
        this.tabs = errorTabConfig;

        return [
          {
            error:
              err?.error.report.code === NotEligibleError.CODE // could be also Routing-103:
                ? NotEligibleError.MESSAGE // The resource can not be routed to the user.
                : ErrorMessages.GENERAL, // User is inactive or doesn't belong to the proper group.
          },
        ];
      })
    );
  }

  onAutocompleteSelect($event: any, dataKey: string): void {
    const filterValue = (typeof $event.value === 'object' && $event.value !== null)
      ? $event.value[dataKey]
      : $event.value;

    this.table.filter(filterValue, dataKey, 'in');  }

  onClearAutocomplete(column: string): void {
    this.table.filter(null, column, 'contains');

    this.filtersText[column] = '';
    this.filtersData = [];
  }

  private getDropdownOptions(): void {
    this.dropdownOptions$ = this.queueService.getReferences().pipe(shareReplay());
  }

  clearFilters(): void {
    const { sortOrder, sortField, filters, first } = this.getDefaultFiltersState();

    this.table.lazy = false;
    this.table.first = first;
    this.table.filters = filters;
    this.filtersText = {};
    this.filtersData = [];
    this.table.sortOrder = sortOrder;
    this.table.sortField = sortField;
    this.table.totalRecords = this.totalRecords;
    this.table.saveState();
    this.table.lazy = true;
    this.table.sortSingle();
  }

  private getDefaultFiltersState() {
    return {
      filters: {},
      first: 0,
      globalFilter: null,
      sortField: 'routedDate',
      sortOrder: 1,
    };
  }
}
